import { Injectable } from '@angular/core';
import { UserSettingsService } from '../../Account/userSettings.service';
import { InstanceRights, RestrictService } from '../../Common/Permissions/restrict.service';
import { SmartSearchService } from '../../Search/Advanced/smartSearch.service';
import { SearchOperatorsService } from '../../Search/searchOperators.service';
import { AuthenticationService } from '../../Account/authentication.service';
import { UserInstanceService } from '../../User/userInstance.service';
import { getActionViewFieldId, toActionViewOutputColumn } from './actionView.adapters';
import { ActionViewApiService } from './actionView.service';
import {
    ActionViewApiColumn,
    ActionViewOutputColumn,
    ActionViewSearchRequest,
    ActionViewTaskType,
    ActionViewUiColumn,
    ActionViewUiFilterElement,
    TaskActionViewShowOption,
} from './actionView.models';

interface ActionViewCustomTaskTypeSetting {
    taskSeriesTypeID: number;
    taskTypeID: number;
    columns: Array<{ columnId: number; sequence: number }>;
}

export interface ActionViewCriteriaData {
    taskTypes: ActionViewTaskType[];
    taskTypeColumns: ActionViewApiColumn[];
    allFields: Core.AdvancedSearchField[];
    hasPrivatePermission: boolean;
    isTeamMemberAndAssignable: boolean;
}

export interface ActionViewOutputSelection {
    taskOutputColumns: ActionViewOutputColumn[];
    entityOutputColumns: ActionViewOutputColumn[];
    filterColumns: ActionViewUiColumn[];
    customOutput: boolean;
    taskTypeName: string;
}

@Injectable({ providedIn: 'root' })
export class ActionViewCriteriaService {
    constructor(
        private readonly actionViewService: ActionViewApiService,
        private readonly smartSearchService: SmartSearchService,
        private readonly searchOperatorsService: SearchOperatorsService,
        private readonly userSettingsService: UserSettingsService,
        private readonly restrictService: RestrictService,
        private readonly userInstanceService: UserInstanceService,
        private readonly authenticationService: AuthenticationService,
    ) {}

    async load(): Promise<ActionViewCriteriaData> {
        const [taskTypeResult, allFields] = await Promise.all([
            this.actionViewService.getTaskTypes(),
            this.smartSearchService.getAllFields(false),
        ]);
        const hasPrivatePermission = this.userInstanceService
            .getUserInstanceMembership()
            .some((instance) =>
                this.restrictService.hasInstanceRight(InstanceRights.PRIVATEITEMSVIEW, instance.instanceId),
            );
        const userInfo = hasPrivatePermission ? null : await this.authenticationService.getUserInfo();
        let taskTypes = this.actionViewService.sortTaskTypes(taskTypeResult.taskTypes);

        if (
            this.userInstanceService.isSingleInstanceSelected() &&
            !this.userInstanceService.isCurrentInstanceRyan() &&
            !this.userInstanceService.isCurrentInstanceSystem()
        ) {
            taskTypes = taskTypes.filter((taskType) => taskType.taskTypeID !== 34);
        }

        return {
            taskTypes,
            taskTypeColumns: taskTypeResult.columns,
            allFields,
            hasPrivatePermission,
            isTeamMemberAndAssignable: userInfo?.isTeamMemberAndAssignable ?? true,
        };
    }

    getOutputSelection(
        data: ActionViewCriteriaData,
        taskSeriesTypeID: number,
        taskTypeID: number,
        previousFilters: ActionViewUiColumn[] = [],
    ): ActionViewOutputSelection {
        const taskType = data.taskTypes.find(
            (item) => item.taskSeriesTypeID === taskSeriesTypeID && item.taskTypeID === taskTypeID,
        );
        const customSetting = this.getCustomTaskTypes().find(
            (item) => item.taskSeriesTypeID === taskSeriesTypeID && item.taskTypeID === taskTypeID,
        );
        const columns = customSetting
            ? customSetting.columns.map((column) =>
                  this.fromAdvancedSearchField(data.allFields, column.columnId, column.sequence),
              )
            : (taskType?.columnIds ?? []).map((columnId, sequence) =>
                  this.fromApiColumn(data.taskTypeColumns, columnId, sequence),
              );
        const validColumns = columns.filter((column): column is ActionViewUiColumn => Boolean(column));
        const taskColumns = validColumns.filter((column) => this.isTaskColumn(column));
        const entityColumns = validColumns
            .filter((column) => !this.isTaskColumn(column))
            .map((column) => ({ ...column, fromTaskType: true }));
        const previousById = new Map(previousFilters.map((column) => [getActionViewFieldId(column), column.or]));
        const filterColumns = entityColumns
            .filter((column) => ![9002, 1430].includes(getActionViewFieldId(column)))
            .map((column) => ({
                ...column,
                or: structuredClone(previousById.get(getActionViewFieldId(column)) ?? column.or),
            }));

        return {
            taskOutputColumns: taskColumns.map(toActionViewOutputColumn),
            entityOutputColumns: entityColumns.map(toActionViewOutputColumn),
            filterColumns,
            customOutput: Boolean(customSetting),
            taskTypeName: taskType?.name ?? 'All',
        };
    }

    hydrateSavedOutputColumns(
        data: ActionViewCriteriaData,
        criteria: ActionViewSearchRequest,
    ): ActionViewSearchRequest {
        const taskTypeOutput = this.getOutputSelection(data, criteria.taskSeriesTypeID, criteria.taskTypeID);
        const taskTypeEntityColumnIds = new Set(taskTypeOutput.entityOutputColumns.map((column) => column.id));

        return {
            ...criteria,
            entityOutputColumns: criteria.entityOutputColumns.map((column) => {
                const fieldId = getActionViewFieldId(column);
                return {
                    ...column,
                    fromTaskType: column.fromTaskType ?? (taskTypeEntityColumnIds.has(fieldId) ? true : undefined),
                };
            }),
        };
    }

    resolveSavedFilterColumns(
        data: ActionViewCriteriaData,
        filters: Core.AdvancedSearchFilter[],
        outputColumns: number[] = [],
    ): ActionViewUiColumn[] {
        if (!filters.length) {
            return outputColumns
                .map((columnId, sequence) => this.fromAdvancedSearchField(data.allFields, columnId, sequence))
                .filter(
                    (column): column is ActionViewUiColumn =>
                        Boolean(column) && ![9002, 1430].includes(getActionViewFieldId(column)),
                );
        }

        return filters
            .map((filter) => {
                const column = this.fromAdvancedSearchField(data.allFields, filter.columnId, 0);
                if (!column || [9002, 1430].includes(filter.columnId)) {
                    return null;
                }
                const byName = new Map(column.operators.map((operator) => [operator.name, operator]));
                const filterElements: ActionViewUiFilterElement[] = filter.or
                    .filter((element) => Boolean(element.operator))
                    .map((element) => ({
                        value: element.value,
                        operator: byName.get(element.operator) ?? { name: element.operator },
                    }));
                return { ...column, or: filterElements.length ? filterElements : column.or };
            })
            .filter((column): column is ActionViewUiColumn => Boolean(column));
    }

    resolveOutputColumns(data: ActionViewCriteriaData, columns: ActionViewOutputColumn[]): ActionViewUiColumn[] {
        return columns
            .map<ActionViewUiColumn | null>((column) => {
                const fieldId = getActionViewFieldId(column);
                const resolved =
                    this.fromAdvancedSearchField(data.allFields, fieldId, column.sequence) ??
                    this.fromApiColumn(data.taskTypeColumns, fieldId, column.sequence);
                return resolved ? { ...resolved, fromTaskType: column.fromTaskType } : null;
            })
            .filter((column): column is ActionViewUiColumn => column !== null);
    }

    ensureAllowedShowChoice(criteria: ActionViewSearchRequest, data: ActionViewCriteriaData): ActionViewSearchRequest {
        if (data.hasPrivatePermission || data.isTeamMemberAndAssignable) {
            return criteria;
        }
        return {
            ...criteria,
            showChoice: TaskActionViewShowOption.AllTasks,
            whereChoice: 0 as ActionViewSearchRequest['whereChoice'],
        };
    }

    private fromApiColumn(
        columns: ActionViewApiColumn[],
        columnId: number,
        sequence: number,
    ): ActionViewUiColumn | null {
        const source = columns.find((column) => column.columnId === columnId);
        if (!source) {
            return null;
        }
        const operators = this.searchOperatorsService.getOperatorArray(source.operators).map((operator) => ({
            ...operator,
            name: operator.name ?? '',
        }));
        return {
            columnId,
            sequence,
            displayName: source.displayName,
            resultColumnName: source.resultColumnName,
            depthValue: source.depthValue,
            type: source.type,
            operators,
            or: [this.createEmptyFilter(columnId, operators)],
            canGroupTaskActionView: source.canGroupTaskActionView,
        };
    }

    private fromAdvancedSearchField(
        fields: Core.AdvancedSearchField[],
        fieldId: number,
        sequence: number,
    ): ActionViewUiColumn | null {
        const source = fields.find((field) => field.advancedSearchFieldID === fieldId);
        if (!source) {
            return null;
        }
        const operators = this.searchOperatorsService.getOperatorArray(source.operators).map((operator) => ({
            ...operator,
            name: operator.name ?? '',
        }));
        return {
            advancedSearchFieldID: fieldId,
            sequence,
            displayName: source.displayName,
            resultsColumnName: source.resultsColumnName,
            resultColumnName: source.resultsColumnName,
            depthValue: source.depthValue,
            type: source.advancedSearchDataType?.name,
            operators,
            or: [this.createEmptyFilter(fieldId, operators)],
            canGroupTaskActionView: source.canGroupTaskActionView,
        };
    }

    private createEmptyFilter(fieldId: number, operators: ActionViewUiColumn['operators']): ActionViewUiFilterElement {
        const filter = [101, 109].includes(fieldId)
            ? this.searchOperatorsService.getDropdownOr(operators)
            : this.searchOperatorsService.getOr(operators);
        return {
            value: filter.value,
            operator: filter.operator,
        };
    }

    private isTaskColumn(column: ActionViewUiColumn): boolean {
        const fieldId = getActionViewFieldId(column);
        return fieldId > 9999 && fieldId < 11000;
    }

    private getCustomTaskTypes(): ActionViewCustomTaskTypeSetting[] {
        const setting = this.userSettingsService.getSettingsByGroup(12)[0];
        if (!setting?.value) {
            return [];
        }
        const value: unknown = typeof setting.value === 'string' ? JSON.parse(setting.value) : setting.value;
        if (!Array.isArray(value)) {
            return [];
        }
        return value.filter(this.isCustomTaskTypeSetting);
    }

    private isCustomTaskTypeSetting(value: unknown): value is ActionViewCustomTaskTypeSetting {
        if (typeof value !== 'object' || value === null) {
            return false;
        }
        const candidate = value as Partial<ActionViewCustomTaskTypeSetting>;
        return (
            Number.isInteger(candidate.taskSeriesTypeID) &&
            Number.isInteger(candidate.taskTypeID) &&
            Array.isArray(candidate.columns)
        );
    }
}
